import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

const GridBackground = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#f8fafc',
        backgroundImage: `linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
        opacity: 0.4,
      }}
    />
  );
};

const ProductCard = ({
  productName,
  price,
  color,
  delay,
  frame,
  fps,
  x,
  y,
  scale: baseScale = 1,
}: {
  productName: string;
  price: string;
  color: string;
  delay: number;
  frame: number;
  fps: number;
  x: number;
  y: number;
  scale?: number;
}) => {
  const entryProgress = spring({ frame: frame - delay, fps, config: { damping: 12 } });
  const scale = interpolate(entryProgress, [0, 1], [0.5, baseScale]);
  const opacity = interpolate(entryProgress, [0, 1], [0, 1]);
  const rotation = interpolate(entryProgress, [0, 1], [-10, 0]);

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: 280,
        padding: 20,
        backgroundColor: 'white',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        opacity,
      }}
    >
      {/* Product image placeholder */}
      <div
        style={{
          width: '100%',
          height: 140,
          borderRadius: 12,
          backgroundColor: color,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ width: 60, height: 60, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.3)' }} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', margin: 0, marginBottom: 8 }}>
        {productName}
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{price}</span>
        <span style={{ padding: '4px 12px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
          NEW
        </span>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, change, delay, frame, fps }: { label: string; value: string; change: string; delay: number; frame: number; fps: number }) => {
  const entryProgress = spring({ frame: frame - delay, fps, config: { damping: 15 } });
  const scale = interpolate(entryProgress, [0, 1], [0.9, 1]);
  const opacity = interpolate(entryProgress, [0, 1], [0, 1]);

  return (
    <div
      style={{
        padding: 20,
        backgroundColor: 'white',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        transform: `scale(${scale})`,
        opacity,
        flex: 1,
      }}
    >
      <p style={{ fontSize: 12, color: '#64748b', margin: 0, marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 28, fontWeight: 700, color: '#0f172a', margin: 0 }}>{value}</p>
      <p style={{ fontSize: 12, color: '#22c55e', margin: 0, marginTop: 4 }}>{change}</p>
    </div>
  );
};

export const MarketingAutomationDemo = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Animation phases
  const titleEntry = spring({ frame, fps, config: { damping: 15 } });

  // Counter animation
  const counterStart = 120;
  const counterValue = Math.floor(interpolate(frame, [counterStart, counterStart + 60], [1, 1247], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }));

  // Floating animation
  const float1 = Math.sin(frame * 0.04) * 8;
  const float2 = Math.sin(frame * 0.04 + 1) * 8;
  const float3 = Math.sin(frame * 0.04 + 2) * 8;

  return (
    <AbsoluteFill style={{ fontFamily: 'system-ui, sans-serif', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <GridBackground />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          width: '100%',
          textAlign: 'center',
          opacity: interpolate(titleEntry, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(titleEntry, [0, 1], [-20, 0])}px)`,
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', backgroundColor: '#dbeafe', borderRadius: 20, marginBottom: 16 }}>
          <span style={{ fontSize: 14, color: '#1d4ed8', fontWeight: 500 }}>1 Template</span>
          <span style={{ color: '#64748b' }}>→</span>
          <span style={{ fontSize: 14, color: '#1d4ed8', fontWeight: 600 }}>{counterValue.toLocaleString()} Videos</span>
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Scale Your Video Production
        </h1>
      </div>

      {/* Product Cards (scattered) */}
      <div style={{ transform: `translateY(${float1}px)` }}>
        <ProductCard
          productName="Premium Headphones"
          price="$299"
          color="#f0abfc"
          delay={40}
          frame={frame}
          fps={fps}
          x={width * 0.08}
          y={height * 0.35}
          scale={0.95}
        />
      </div>
      <div style={{ transform: `translateY(${float2}px)` }}>
        <ProductCard
          productName="Smart Watch Pro"
          price="$449"
          color="#93c5fd"
          delay={60}
          frame={frame}
          fps={fps}
          x={width * 0.38}
          y={height * 0.28}
        />
      </div>
      <div style={{ transform: `translateY(${float3}px)` }}>
        <ProductCard
          productName="Wireless Earbuds"
          price="$179"
          color="#86efac"
          delay={80}
          frame={frame}
          fps={fps}
          x={width * 0.68}
          y={height * 0.35}
          scale={0.95}
        />
      </div>

      {/* A/B Testing Badge */}
      <div
        style={{
          position: 'absolute',
          top: height * 0.55,
          left: width * 0.5,
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 16,
          opacity: interpolate(spring({ frame: frame - 100, fps, config: { damping: 12 } }), [0, 1], [0, 1]),
        }}
      >
        <div style={{ padding: '8px 20px', backgroundColor: '#fef3c7', borderRadius: 20, fontSize: 14, fontWeight: 600, color: '#92400e' }}>
          Variant A
        </div>
        <div style={{ padding: '8px 20px', backgroundColor: '#d1fae5', borderRadius: 20, fontSize: 14, fontWeight: 600, color: '#166534' }}>
          Variant B
        </div>
        <div style={{ padding: '8px 20px', backgroundColor: '#e0e7ff', borderRadius: 20, fontSize: 14, fontWeight: 600, color: '#4338ca' }}>
          Variant C
        </div>
      </div>

      {/* Metrics Row */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 20,
          width: width * 0.7,
        }}
      >
        <MetricCard label="Videos Generated" value="1,247" change="↑ 340% this week" delay={150} frame={frame} fps={fps} />
        <MetricCard label="A/B Variants" value="12" change="3 winners found" delay={170} frame={frame} fps={fps} />
        <MetricCard label="Time Saved" value="156h" change="vs manual editing" delay={190} frame={frame} fps={fps} />
      </div>

      {/* Decorative blurs */}
      <div
        style={{
          position: 'absolute',
          top: height * 0.2,
          right: width * 0.05,
          width: 300,
          height: 300,
          borderRadius: '50%',
          backgroundColor: '#f0abfc',
          opacity: 0.15,
          filter: 'blur(80px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: height * 0.25,
          left: width * 0.05,
          width: 250,
          height: 250,
          borderRadius: '50%',
          backgroundColor: '#93c5fd',
          opacity: 0.15,
          filter: 'blur(80px)',
        }}
      />
    </AbsoluteFill>
  );
};
